import React from 'react';
import {Pressable, ScrollView, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import type {ClubEvent, ClubTournamentHighlight} from '@/Hooks/ClubsApi/types';

type Props = {
  event: ClubEvent | null;
  tournaments: ClubTournamentHighlight[];
  isAdmin: boolean;
  variant: 'mobile' | 'rail';
  onEndEvent: () => void;
  onTournamentPress: (tournament: ClubTournamentHighlight) => void;
};

const formatStart = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Upcoming';
  }
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export function ClubActivityLauncher({
  mobile,
  isAdmin,
  puzzleRoyalLive,
  onScheduleTournament,
  onPuzzleRoyal,
}: {
  mobile: boolean;
  isAdmin: boolean;
  puzzleRoyalLive: boolean;
  onScheduleTournament?: () => void;
  onPuzzleRoyal?: () => void;
}) {
  if (!isAdmin && !puzzleRoyalLive) return null;

  const action = ({
    kind,
    title,
    subtitle,
    cta,
    onPress,
  }: {
    kind: 'schedule' | 'royale';
    title: string;
    subtitle: string;
    cta: string;
    onPress?: () => void;
  }) => {
    const schedule = kind === 'schedule';
    const live = kind === 'royale' && puzzleRoyalLive;
    const rowBackground = live ? '#E9F7F0' : schedule ? '#EEF8F6' : '#FFF7DF';
    const iconBackground = live ? '#CDEBDD' : schedule ? '#D4EFEB' : '#FCE9AE';
    const iconColor = live ? '#216B46' : schedule ? '#216F67' : '#735612';
    const ctaBackground = live ? '#278A59' : schedule ? '#258F84' : '#E8AE18';
    const ctaColor = live || schedule ? '#FFFFFF' : '#443306';

    return (
      <Pressable
        testID={schedule ? 'club-schedule-btn' : 'club-fun-challenge-btn'}
        accessibilityRole="button"
        accessibilityLabel={live ? 'Join live Puzzle Royal' : title}
        accessibilityHint={
          live
            ? 'Joins the active club Puzzle Royal'
            : schedule
            ? 'Opens tournament and Puzzle Storm options'
            : 'Opens Puzzle Royal creation'
        }
        onPress={event => {
          event?.stopPropagation?.();
          onPress?.();
        }}
        style={({pressed}) => ({
          minHeight: mobile ? 56 : 60,
          flexDirection: 'row',
          alignItems: 'center',
          gap: mobile ? 9 : 10,
          paddingHorizontal: mobile ? 9 : 10,
          paddingVertical: 7,
          borderRadius: 13,
          backgroundColor: rowBackground,
          opacity: pressed ? 0.82 : 1,
          transform: [{translateX: pressed ? 2 : 0}],
        })}>
        <View
          style={{
            width: mobile ? 36 : 40,
            height: mobile ? 36 : 40,
            borderRadius: 12,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: iconBackground,
          }}>
          <CCText
            style={{
              color: iconColor,
              fontSize: mobile ? 17 : 19,
              lineHeight: mobile ? 20 : 22,
            }}>
            {schedule ? '◷' : '♛'}
          </CCText>
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 5}}>
            <CCText
              numberOfLines={1}
              style={{
                flexShrink: 1,
                color: '#332F2D',
                fontSize: mobile ? 11.5 : 12.5,
                fontWeight: '800',
              }}>
              {title}
            </CCText>
            {live ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 3,
                  paddingHorizontal: 5,
                  paddingVertical: 2,
                  borderRadius: 999,
                  backgroundColor: '#D2EDDF',
                }}>
                <View
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#278A59',
                  }}
                />
                <CCText
                  style={{
                    color: '#216B46',
                    fontSize: 7,
                    fontWeight: '900',
                    letterSpacing: 0.35,
                  }}>
                  LIVE
                </CCText>
              </View>
            ) : null}
          </View>
          <CCText
            numberOfLines={1}
            style={{
              color: '#7D7670',
              fontSize: mobile ? 8.5 : 9.5,
              marginTop: 2,
            }}>
            {subtitle}
          </CCText>
        </View>
        <View
          style={{
            minWidth: mobile ? 54 : 62,
            minHeight: 30,
            paddingHorizontal: mobile ? 9 : 11,
            borderRadius: 999,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            backgroundColor: ctaBackground,
            shadowColor: ctaBackground,
            shadowOffset: {width: 0, height: 2},
            shadowOpacity: 0.2,
            shadowRadius: 5,
            elevation: 3,
          }}>
          <CCText
            style={{
              color: ctaColor,
              fontSize: mobile ? 9 : 10,
              fontWeight: '900',
            }}>
            {cta}
          </CCText>
          <CCText style={{color: ctaColor, fontSize: 11, fontWeight: '900'}}>
            →
          </CCText>
        </View>
      </Pressable>
    );
  };

  return (
    <View
      testID="club-pulse-actions"
      style={{
        marginHorizontal: mobile ? 14 : 0,
        marginTop: mobile ? 7 : 0,
        marginBottom: 10,
        padding: mobile ? 8 : 9,
        borderRadius: 16,
        gap: 7,
        backgroundColor: '#FFFFFF',
        shadowColor: '#4D3F37',
        shadowOffset: {width: 0, height: 3},
        shadowOpacity: 0.09,
        shadowRadius: 10,
        elevation: 3,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 7,
          paddingHorizontal: 3,
          paddingBottom: 1,
        }}>
        <View
          style={{
            width: 22,
            height: 22,
            borderRadius: 8,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F7EBC7',
          }}>
          <CCText style={{color: '#9A7418', fontSize: 12}}>✦</CCText>
        </View>
        <View style={{flex: 1}}>
          <CCText
            style={{
              color: '#564E49',
              fontSize: mobile ? 9 : 10,
              fontWeight: '900',
              letterSpacing: 0.45,
            }}>
            {puzzleRoyalLive && !isAdmin
              ? 'LIVE IN YOUR CLUB'
              : 'PLAY TOGETHER'}
          </CCText>
          <CCText style={{color: '#918983', fontSize: mobile ? 8 : 9}}>
            {puzzleRoyalLive && !isAdmin
              ? 'Jump in and play with your clubmates'
              : 'Choose an activity for your club'}
          </CCText>
        </View>
      </View>
      {isAdmin
        ? action({
            kind: 'schedule',
            title: 'Plan Club Activity',
            subtitle: 'Tournament or Puzzle Storm',
            cta: 'Choose',
            onPress: onScheduleTournament,
          })
        : null}
      {action({
        kind: 'royale',
        title: puzzleRoyalLive ? 'Join Puzzle Royal' : 'Create Puzzle Royal',
        subtitle: puzzleRoyalLive
          ? 'Clubmates are playing now'
          : 'A fast live puzzle race for everyone',
        cta: puzzleRoyalLive ? 'Join now' : 'Start',
        onPress: onPuzzleRoyal,
      })}
    </View>
  );
}

export default function ClubPulse({
  event,
  tournaments,
  isAdmin,
  variant,
  onEndEvent,
  onTournamentPress,
}: Props) {
  const mobile = variant === 'mobile';
  const count = Number(!!event) + tournaments.length;
  if (!count) {
    return null;
  }

  const cardWidth = mobile ? 292 : ('100%' as const);
  const cards = (
    <>
      {event ? (
        <View
          testID="club-pulse-event"
          style={{
            width: cardWidth,
            height: mobile ? 68 : undefined,
            minHeight: mobile ? undefined : 72,
            paddingHorizontal: mobile ? 12 : 11,
            paddingVertical: mobile ? 10 : 11,
            borderRadius: mobile ? 12 : 13,
            borderWidth: 1,
            borderColor: '#E5DED0',
            backgroundColor: '#F8F5EE',
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <CCText
              numberOfLines={1}
              style={{
                flex: 1,
                color: '#625B55',
                fontSize: mobile ? 11.5 : 12,
                fontWeight: '700',
              }}>
              ⚡ {event.title}
            </CCText>
            <CCText
              style={{
                color: '#8A6B25',
                fontSize: mobile ? 9.5 : 10,
                fontWeight: '800',
                marginLeft: 8,
              }}>
              {Math.min(100, event.pct)}%
            </CCText>
          </View>
          <View
            style={{
              height: mobile ? 4 : 5,
              borderRadius: 999,
              backgroundColor: '#E8E0D1',
              overflow: 'hidden',
              marginTop: mobile ? 5 : 9,
            }}>
            <View
              style={{
                width: `${Math.min(100, event.pct)}%`,
                height: '100%',
                borderRadius: 999,
                backgroundColor: '#C49A3A',
              }}
            />
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: mobile ? 4 : 7,
            }}>
            <CCText style={{color: '#625B55', fontSize: mobile ? 9 : 10}}>
              {event.progress.toLocaleString('en-IN')} /{' '}
              {event.goal.toLocaleString('en-IN')} puzzles
            </CCText>
            {isAdmin ? (
              <Pressable testID="event-end" onPress={onEndEvent} hitSlop={6}>
                <CCText
                  style={{
                    color: '#B36D1F',
                    fontSize: mobile ? 9 : 10,
                    fontWeight: '700',
                  }}>
                  End
                </CCText>
              </Pressable>
            ) : (
              <CCText
                style={{
                  color: '#237B43',
                  fontSize: mobile ? 9 : 10,
                  fontWeight: '700',
                }}>
                You +{event.my_contribution}
              </CCText>
            )}
          </View>
        </View>
      ) : null}

      {tournaments.map(tournament => {
        const arena = tournament.tournament_type === 'Circlechess_Arena';
        return (
          <Pressable
            key={tournament.id}
            testID={`club-tournament-${tournament.id}`}
            onPress={() => onTournamentPress(tournament)}
            style={{
              width: cardWidth,
              height: mobile ? 60 : undefined,
              minHeight: mobile ? undefined : 72,
              padding: mobile ? 9 : 11,
              borderRadius: mobile ? 12 : 13,
              borderWidth: 1,
              borderColor: arena
                ? 'rgba(102,98,180,0.35)'
                : 'rgba(61,171,158,0.38)',
              backgroundColor: arena ? '#F4F2FF' : '#EFFAF7',
            }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <CCText
                numberOfLines={1}
                style={{
                  flex: 1,
                  color: '#332F2D',
                  fontSize: mobile ? 11.5 : 12,
                  fontWeight: '700',
                }}>
                🏆 {tournament.name}
              </CCText>
              <CCText
                style={{
                  color: arena ? '#6662B4' : '#27877C',
                  fontSize: mobile ? 9.5 : 10,
                  fontWeight: '800',
                  marginLeft: 8,
                }}>
                Join →
              </CCText>
            </View>
            <CCText
              numberOfLines={1}
              style={{
                color: arena ? '#7772B8' : '#39968B',
                fontSize: mobile ? 9 : 10,
                marginTop: mobile ? 4 : 7,
              }}>
              {arena ? 'Arena' : 'Swiss'} · {formatStart(tournament.start_date)}
            </CCText>
          </Pressable>
        );
      })}
    </>
  );

  if (mobile) {
    return (
      <View
        testID="club-pulse-mobile"
        style={{paddingTop: 2, paddingBottom: 7}}>
        <View
          style={{
            paddingHorizontal: 14,
            marginBottom: 9,
          }}>
          <CCText style={{color: '#332F2D', fontSize: 13, fontWeight: '800'}}>
            Club Pulse
          </CCText>
          <CCText style={{color: '#888280', fontSize: 9.5, marginTop: 1}}>
            Active challenges and upcoming tournaments
          </CCText>
        </View>
        {count ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 14,
              marginBottom: 6,
            }}>
            <CCText
              style={{
                color: '#776F69',
                fontSize: 8.5,
                fontWeight: '800',
                letterSpacing: 0.55,
              }}>
              HAPPENING NOW · {count}
            </CCText>
            {count > 1 ? (
              <CCText
                testID="club-pulse-swipe-hint"
                style={{color: '#958C86', fontSize: 9}}>
                Swipe for more →
              </CCText>
            ) : null}
          </View>
        ) : null}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 7,
            paddingHorizontal: 14,
            paddingRight: 22,
          }}>
          {cards}
        </ScrollView>
      </View>
    );
  }

  return (
    <View
      testID="club-pulse-rail"
      style={{
        padding: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: 'rgba(38,35,34,0.10)',
        backgroundColor: '#fff',
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}>
        <View>
          <CCText style={{color: '#262322', fontSize: 13, fontWeight: '700'}}>
            Club Pulse
          </CCText>
          <CCText style={{color: '#888280', fontSize: 10.5, marginTop: 2}}>
            Active challenges and upcoming tournaments
          </CCText>
        </View>
        {count ? (
          <View
            style={{
              minWidth: 24,
              height: 24,
              paddingHorizontal: 7,
              borderRadius: 12,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(61,171,158,0.12)',
            }}>
            <CCText style={{color: '#287A70', fontSize: 11, fontWeight: '800'}}>
              {count}
            </CCText>
          </View>
        ) : null}
      </View>
      {count ? (
        <CCText
          style={{
            color: '#776F69',
            fontSize: 8.5,
            fontWeight: '800',
            letterSpacing: 0.55,
            marginBottom: 7,
          }}>
          HAPPENING NOW · {count}
        </CCText>
      ) : null}
      <ScrollView
        nestedScrollEnabled
        style={{maxHeight: 232}}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{gap: 8}}>
        {cards}
      </ScrollView>
    </View>
  );
}
